import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft, AlertCircle, Loader2, Book,
  Download, Printer, MoreVertical, MinusCircle, PlusCircle, Link as LinkIcon,
  AlignLeft, AlignRight, FileText, FileDown as FileWord,
  Sun, Sunset, Moon, PlayCircle, PauseCircle, Type,
  ScrollText, BookOpen,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { createSermonPDF } from '../lib/pdf';
import { parseISO } from 'date-fns';
import { Timer } from './Timer';
import { useSectionStyles } from '../hooks/useSectionStyles';
import { useWakeLock } from '../hooks/useWakeLock';
import { createWordDocument } from '../lib/docx';
import { saveAs } from 'file-saver';
import { SermonShareModal } from './SermonShareModal';
import { SermonBlocks, type PreachBlock } from './preach/SermonBlocks';
import { BookReader } from './preach/BookReader';

type ReadingMode = 'light' | 'warm' | 'dark';
type FontFamily = 'sans' | 'serif';
type ViewMode = 'scroll' | 'book';

/**
 * Last-used-wins (plan § Persistence): read per-sermon override, else global, else
 * hard default; write BOTH keys, and only on user change — a sermon you deliberately
 * customized keeps its setting, a new sermon inherits whatever you last used
 * anywhere. Writing on mere open would instead stamp a per-sermon override onto
 * every sermon ever viewed and freeze it at its first-opened mode.
 */
function readPreachSetting(key: string, sermonId: string | undefined): string | null {
  const perSermon =
    sermonId && sermonId !== 'preview' ? localStorage.getItem(`${key}_${sermonId}`) : null;
  return perSermon ?? localStorage.getItem(key);
}

function writePreachSetting(key: string, value: string, sermonId: string | undefined) {
  localStorage.setItem(key, value);
  if (sermonId && sermonId !== 'preview') localStorage.setItem(`${key}_${sermonId}`, value);
}

/** The margin-width → content-width math, shared by scroll mode's imperative effect
 *  and book mode's column layout — margins drive column width (§3). */
function contentLayout(marginWidth: number, isMobile: boolean): { width: string; maxWidth: string } {
  if (isMobile) {
    const w = marginWidth <= 100
      ? 85 + ((100 - marginWidth) / 50) * 10
      : 85 - ((marginWidth - 100) / 50) * 5;
    return { width: `${w}%`, maxWidth: 'none' };
  }
  const w = marginWidth <= 100
    ? 70 + ((100 - marginWidth) / 50) * 20
    : 70 - ((marginWidth - 100) / 50) * 20;
  const max = marginWidth <= 100 ? 1400 : 1200 - ((marginWidth - 100) * 4);
  return { width: `${w}%`, maxWidth: `${max}px` };
}

const THEMES: Record<ReadingMode, {
  bg: string; text: string; subText: string; controlsBg: string;
  border: string; infoBg: string; sectionFilter: string;
}> = {
  light: {
    bg: '#FFFFFF',
    text: '#1C2B39',
    subText: '#64748b',
    controlsBg: 'rgba(255,255,255,0.97)',
    infoBg: 'rgba(255,255,255,0.97)',
    border: 'rgba(200,190,180,0.25)',
    sectionFilter: '',
  },
  warm: {
    bg: '#FAF6EE',
    text: '#2D1B08',
    subText: '#8B6D50',
    controlsBg: 'rgba(250,246,238,0.97)',
    infoBg: 'rgba(250,246,238,0.97)',
    border: 'rgba(180,145,100,0.3)',
    sectionFilter: 'sepia(0.15) brightness(0.98)',
  },
  dark: {
    bg: '#141B22',
    text: '#E0DBD0',
    subText: '#8899AA',
    controlsBg: 'rgba(20,27,34,0.97)',
    infoBg: 'rgba(20,27,34,0.97)',
    border: 'rgba(80,110,140,0.25)',
    sectionFilter: 'brightness(0.85) saturate(0.9)',
  },
};

export function PreachMode({ isPublic = false }: { isPublic?: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { styles } = useSectionStyles();

  const [loading, setLoading] = useState(true);
  const [sermon, setSermon] = useState<any>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [copying, setCopying] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const [fontSize, setFontSize] = useState(() =>
    parseInt(localStorage.getItem(`preach_fontSize_${id}`) || '120')
  );
  const [marginWidth, setMarginWidth] = useState(() =>
    parseInt(localStorage.getItem(`preach_marginWidth_${id}`) || '100')
  );
  const [readingMode, setReadingMode] = useState<ReadingMode>(() =>
    (localStorage.getItem(`preach_readingMode_${id}`) as ReadingMode) || 'light'
  );
  const [fontFamily, setFontFamily] = useState<FontFamily>(() =>
    (localStorage.getItem(`preach_fontFamily_${id}`) as FontFamily) || 'sans'
  );
  const [autoScroll, setAutoScroll] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState(3);
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    readPreachSetting('preach_viewMode', id) === 'book' ? 'book' : 'scroll'
  );
  const [bookColumns, setBookColumns] = useState<1 | 2>(() =>
    readPreachSetting('preach_bookColumns', id) === '2' ? 2 : 1
  );

  // Book-mode page position, reported up by BookReader so the info-bar ring and
  // label can be driven from it (§4.3 — everything scroll-derived is dead in book).
  const [bookPage, setBookPage] = useState(0);
  const [bookPageCount, setBookPageCount] = useState(1);
  // Anchor for the folio's elapsed clock, stamped when the sermon loads so a mode
  // switch mid-sermon doesn't reset it.
  const [preachStartedAt, setPreachStartedAt] = useState(0);
  // Bumped by pointer contact with the bars so the 4s auto-hide restarts (§4.3).
  const [chromeIdleTick, setChromeIdleTick] = useState(0);

  const [wordCount, setWordCount] = useState(0);
  const [preachTime, setPreachTime] = useState(0);
  const [processedContent, setProcessedContent] = useState<PreachBlock[]>([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [controlsHidden, setControlsHidden] = useState(false);
  const [controlsHeight, setControlsHeight] = useState(56);
  const [infoBarHeight, setInfoBarHeight] = useState(80);
  const [exportLoading, setExportLoading] = useState<'pdf' | 'word' | null>(null);

  const contentContainerRef = useRef<HTMLDivElement>(null);
  const controlsBarRef = useRef<HTMLDivElement>(null);
  const infoBarRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const lastScrollYRef = useRef(0);
  const autoScrollRef = useRef<number | null>(null);
  const scrollSpeedRef = useRef(scrollSpeed);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const theme = THEMES[readingMode];

  // Keep the screen awake while there is a sermon on the pulpit. The retry handle
  // is passed into BookReader so every page turn re-requests inside a user gesture
  // (spike verdict 6 — insurance against system-initiated releases mid-sermon).
  const wakeLock = useWakeLock(!loading && Boolean(sermon?.content));

  // Keep scrollSpeedRef in sync
  useEffect(() => { scrollSpeedRef.current = scrollSpeed; }, [scrollSpeed]);

  // The window never scrolls in book mode; kill rubber-banding of the document
  // behind the pager (§5.4).
  useEffect(() => {
    if (viewMode !== 'book') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [viewMode]);

  // Book-mode chrome auto-hide (§4.3): 4s of idle hides both bars; center-tap
  // toggles them back (BookReader → toggleChrome). The scroll-driven hide/reveal
  // never fires in book mode because the window never scrolls. Any user setting
  // change or pointer contact with the bars (chromeIdleTick) restarts the clock;
  // an open menu pins the chrome. Page turns are deliberately absent from this
  // dependency list — a turn must never reveal or reset the chrome.
  useEffect(() => {
    if (viewMode !== 'book' || controlsHidden || showMenu) return;
    const t = window.setTimeout(() => setControlsHidden(true), 4000);
    return () => window.clearTimeout(t);
  }, [viewMode, controlsHidden, showMenu, chromeIdleTick,
      fontSize, marginWidth, readingMode, fontFamily, bookColumns]);

  const toggleChrome = useCallback(() => setControlsHidden(h => !h), []);

  const handleBookPageState = useCallback((page: number, count: number) => {
    setBookPage(page);
    setBookPageCount(count);
  }, []);

  // Auto-scroll (teleprompter). The viewMode gate keeps the loop structurally
  // impossible in book mode — the window never scrolls there, so the loop would
  // call window.scrollBy on a non-scrolling document forever (§4.3). The mode
  // pill's changeViewMode additionally clears the state on switching to book.
  useEffect(() => {
    if (!autoScroll || viewMode === 'book') {
      if (autoScrollRef.current) cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
      return;
    }
    let lastTime = 0;
    const step = (time: number) => {
      if (lastTime) {
        const delta = time - lastTime;
        window.scrollBy(0, scrollSpeedRef.current * 0.012 * delta);
      }
      lastTime = time;
      autoScrollRef.current = requestAnimationFrame(step);
    };
    autoScrollRef.current = requestAnimationFrame(step);
    return () => {
      if (autoScrollRef.current) cancelAnimationFrame(autoScrollRef.current);
    };
  }, [autoScroll, viewMode]);

  // Stop auto-scroll at bottom
  useEffect(() => {
    if (autoScroll && scrollProgress >= 99.5) setAutoScroll(false);
  }, [scrollProgress, autoScroll]);

  // Scroll + resize listener
  useEffect(() => {
    const sectionNodes = processedContent.filter(e => e.type === 'section');
    const handleScroll = () => {
      const y = window.scrollY;
      if (y > lastScrollYRef.current && y > 80) setControlsHidden(true);
      else if (y < lastScrollYRef.current) setControlsHidden(false);
      lastScrollYRef.current = y;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(total > 0 ? Math.min(100, (y / total) * 100) : 0);
      if (sectionNodes.length > 1) {
        const pct = total > 0 ? Math.min(100, Math.max(0, (y / total) * 100)) : 0;
        setCurrentSectionIndex(Math.floor((pct / 100) * (sectionNodes.length - 1)));
      }
    };
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [processedContent]);

  // Measure bar heights
  useEffect(() => {
    if (controlsBarRef.current) setControlsHeight(controlsBarRef.current.offsetHeight);
    if (infoBarRef.current) setInfoBarHeight(infoBarRef.current.offsetHeight);
  }, [sermon, fontSize, isMobile, readingMode]);

  // Load sermon
  useEffect(() => {
    const loadSermon = async () => {
      try {
        setLoading(true);
        if (id === 'preview') {
          if (location.state) {
            setSermon(location.state);
            setProcessedContent(processContent(location.state.content));
            calculateStats(location.state.content);
          } else {
            navigate('/dashboard');
            return;
          }
        } else if (isPublic) {
          const { createClient } = await import('@supabase/supabase-js');
          const anonSupabase = createClient(
            import.meta.env.VITE_SUPABASE_URL,
            import.meta.env.VITE_SUPABASE_ANON_KEY
          );
          let sermonData = null;
          const { data: directData, error: directError } = await anonSupabase
            .from('sermons').select('*').eq('id', id).single();
          if (!directError && directData) {
            sermonData = directData;
          } else {
            const { data: sharedData, error: sharedError } = await anonSupabase
              .from('shared_sermons').select('sermon_id').eq('id', id).single();
            if (sharedError || !sharedData) throw new Error('Sermon not found');
            const { data: sd, error: se } = await anonSupabase
              .from('sermons').select('*').eq('id', sharedData.sermon_id).single();
            if (se || !sd) throw new Error('Sermon not found');
            sermonData = sd;
          }
          setSermon(sermonData);
          setProcessedContent(processContent(sermonData.content));
          calculateStats(sermonData.content);
        } else {
          const { data: sermonData, error } = await supabase
            .from('sermons').select('*').eq('id', id).single();
          if (!error && sermonData) {
            setSermon(sermonData);
            setProcessedContent(processContent(sermonData.content));
            calculateStats(sermonData.content);
          } else {
            const { createClient } = await import('@supabase/supabase-js');
            const anonSupabase = createClient(
              import.meta.env.VITE_SUPABASE_URL,
              import.meta.env.VITE_SUPABASE_ANON_KEY
            );
            const { data: anonData, error: anonError } = await anonSupabase
              .from('sermons').select('*').eq('id', id).single();
            if (anonError || !anonData) throw new Error('Sermon not found');
            setSermon(anonData);
            setProcessedContent(processContent(anonData.content));
            calculateStats(anonData.content);
          }
        }
      } catch (error) {
        console.error('Error loading sermon:', error);
        setSermon(null);
      } finally {
        setLoading(false);
        setPreachStartedAt(Date.now());
      }
    };
    loadSermon();
  }, [id, location.state, navigate, isPublic]);

  // Persist settings
  useEffect(() => {
    if (id && id !== 'preview') {
      localStorage.setItem(`preach_fontSize_${id}`, fontSize.toString());
      localStorage.setItem(`preach_marginWidth_${id}`, marginWidth.toString());
      localStorage.setItem(`preach_readingMode_${id}`, readingMode);
      localStorage.setItem(`preach_fontFamily_${id}`, fontFamily);
    }
  }, [fontSize, marginWidth, readingMode, fontFamily, id]);

  // Apply margin width to content container (scroll mode only — the ref is not
  // mounted in book mode; viewMode is a dep so switching back re-applies it).
  useEffect(() => {
    if (!contentContainerRef.current) return;
    const { width, maxWidth } = contentLayout(marginWidth, isMobile);
    contentContainerRef.current.style.maxWidth = maxWidth;
    contentContainerRef.current.style.width = width;
  }, [marginWidth, isMobile, viewMode]);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const calculateStats = (htmlContent: string) => {
    const div = document.createElement('div');
    div.innerHTML = htmlContent;
    const text = div.textContent || div.innerText || '';
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    setWordCount(words);
    setPreachTime(Math.ceil(words / 110));
  };

  const handleFontSizeChange = (increase: boolean) => {
    setFontSize(prev => Math.min(Math.max(increase ? prev + 10 : prev - 10, 70), 220));
  };

  const handleMarginChange = useCallback((increase: boolean) => {
    setMarginWidth(prev => Math.min(Math.max(increase ? prev + 10 : prev - 10, 50), 150));
  }, []);

  const changeViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode === 'book') {
      // §4.3: hiding the auto-scroll button is not enough — stop the state outright
      // (the rAF effect is also gated on viewMode as a structural backstop).
      setAutoScroll(false);
      // A scroll just before the switch may have auto-hidden the chrome, and book
      // mode has no scroll-up gesture to bring it back (center-tap reveal is Phase 4).
      setControlsHidden(false);
    }
    writePreachSetting('preach_viewMode', mode, id);
  };

  const changeBookColumns = (cols: 1 | 2) => {
    setBookColumns(cols);
    writePreachSetting('preach_bookColumns', String(cols), id);
  };

  const handleShare = () => { setShowShareModal(true); setShowMenu(false); };
  const handlePrint = () => { window.print(); setShowMenu(false); };

  const handleDownloadPDF = async () => {
    try {
      setExportLoading('pdf');
      const blob = createSermonPDF(sermon, processedContent);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sermon.title.toLowerCase().replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setExportLoading(null);
      setShowMenu(false);
    }
  };

  const handleDownloadWord = async () => {
    try {
      setExportLoading('word');
      const blob = await createWordDocument(sermon, processedContent);
      saveAs(blob, `${sermon.title.toLowerCase().replace(/\s+/g, '-')}.docx`);
    } catch (error) {
      console.error('Error generating Word document:', error);
      alert('Failed to generate Word document. Please try again.');
    } finally {
      setExportLoading(null);
      setShowMenu(false);
    }
  };

  const isSectionVisible = (sectionId: string) => {
    if (!sectionId || !id || id === 'preview') return true;
    const saved = localStorage.getItem(`sermon-section-visibility-${id}-${sectionId}`);
    return saved !== null ? JSON.parse(saved) : true;
  };

  const processContent = (htmlContent: string): PreachBlock[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    return Array.from(doc.body.children)
      .filter(el => {
        if (el.classList.contains('sermon-section')) {
          return isSectionVisible(el.getAttribute('data-section-id') || '');
        }
        return true;
      })
      .map(el => {
        if (el.classList.contains('sermon-section')) {
          const sectionType = Array.from(el.classList)
            .find(c => c.startsWith('sermon-section-'))?.replace('sermon-section-', '');
          return {
            type: 'section',
            sectionType: sectionType || '',
            pointNumber: el.getAttribute('data-point-number') || '',
            attribution: el.getAttribute('data-attribution') || '',
            sectionId: el.getAttribute('data-section-id') || '',
            content: el.innerHTML,
            className: el.className,
          };
        }
        return { type: 'content', content: el.outerHTML, tag: el.tagName.toLowerCase() };
      });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme.bg }}>
        <Loader2 className="h-8 w-8 animate-spin text-electric" />
      </div>
    );
  }

  if (!sermon?.content) {
    return (
      <div className="min-h-screen p-8" style={{ background: theme.bg }}>
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm border border-sand/20 p-8">
          <h2 className="text-xl font-semibold text-midnight mb-4">
            {isPublic ? 'Shared Sermon Not Found' : 'No Sermon Content Available'}
          </h2>
          <p className="text-slate mb-6">
            {isPublic ? 'This shared sermon link may have expired or is no longer available.' : "This sermon doesn't have any content yet."}
          </p>
          {!isPublic && (
            <button onClick={() => navigate(-1)} className="flex items-center text-electric hover:text-electric/80" type="button">
              <ArrowLeft className="h-5 w-5 mr-2" />Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  const { title, scripture, content, youtubeUrl, date } = sermon;
  const sectionCount = processedContent.filter(e => e.type === 'section').length;
  // Unified progress (§4.3): everything scroll-derived is dead in book mode.
  const progress = viewMode === 'book'
    ? (bookPageCount > 1 ? (bookPage / (bookPageCount - 1)) * 100 : 100)
    : scrollProgress;

  return (
    <div
      data-reading-mode={readingMode}
      style={{
        minHeight: '100vh',
        backgroundColor: theme.bg,
        color: theme.text,
        paddingTop: controlsHeight + infoBarHeight,
        transition: 'padding-top 0.3s ease, background-color 0.3s ease, color 0.3s ease',
      }}
    >
      {/* ── Controls Bar ─────────────────────────────────────────────── */}
      <div
        ref={controlsBarRef}
        className="fixed top-0 left-0 right-0 z-50 transition-transform duration-300"
        onPointerDownCapture={viewMode === 'book' ? () => setChromeIdleTick(t => t + 1) : undefined}
        style={{
          background: theme.controlsBg,
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${theme.border}`,
          transform: controlsHidden ? 'translateY(-100%)' : 'translateY(0)',
        }}
      >
        <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
          {/* Left: Exit */}
          {isPublic ? (
            <div className="flex items-center gap-2" style={{ color: theme.text }}>
              <Book className="h-4 w-4" />
              <span className="font-semibold text-sm">SermonBuild</span>
            </div>
          ) : (
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-sm font-medium transition-opacity hover:opacity-70"
              style={{ color: theme.text }}
              type="button"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Exit</span>
            </button>
          )}

          {/* Center: Reading controls */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Reading mode */}
            <div
              className="flex items-center rounded-lg p-0.5 gap-0.5"
              style={{ background: theme.border }}
            >
              {(['light', 'warm', 'dark'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => setReadingMode(mode)}
                  className="p-1.5 rounded-md transition-all duration-200"
                  style={{
                    background: readingMode === mode ? theme.controlsBg : 'transparent',
                    color: readingMode === mode ? theme.text : theme.subText,
                    boxShadow: readingMode === mode ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                  title={mode.charAt(0).toUpperCase() + mode.slice(1) + ' mode'}
                  type="button"
                >
                  {mode === 'light' && <Sun className="h-3.5 w-3.5" />}
                  {mode === 'warm' && <Sunset className="h-3.5 w-3.5" />}
                  {mode === 'dark' && <Moon className="h-3.5 w-3.5" />}
                </button>
              ))}
            </div>

            {/* Font size */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleFontSizeChange(false)}
                disabled={fontSize <= 70}
                className="p-1 rounded transition-opacity disabled:opacity-30 hover:opacity-70"
                style={{ color: theme.subText }}
                title="Decrease font size"
                type="button"
              >
                <MinusCircle className="h-4 w-4" />
              </button>
              <span
                className="text-xs tabular-nums w-8 text-center font-medium"
                style={{ color: theme.text }}
              >
                {fontSize}%
              </span>
              <button
                onClick={() => handleFontSizeChange(true)}
                disabled={fontSize >= 220}
                className="p-1 rounded transition-opacity disabled:opacity-30 hover:opacity-70"
                style={{ color: theme.subText }}
                title="Increase font size"
                type="button"
              >
                <PlusCircle className="h-4 w-4" />
              </button>
            </div>

            {/* Font family */}
            <button
              onClick={() => setFontFamily(f => f === 'sans' ? 'serif' : 'sans')}
              className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all"
              style={{
                color: theme.text,
                background: theme.border,
                fontFamily: fontFamily === 'serif' ? 'Georgia, "Times New Roman", serif' : 'inherit',
              }}
              title="Toggle font style"
              type="button"
            >
              <Type className="h-3 w-3" />
              {fontFamily === 'serif' ? 'Serif' : 'Sans'}
            </button>

            {/* Margin controls — desktop only */}
            <div className="hidden md:flex items-center gap-1">
              <button
                onClick={() => handleMarginChange(true)}
                disabled={marginWidth >= 150}
                className="p-1 rounded transition-opacity disabled:opacity-30 hover:opacity-70"
                style={{ color: theme.subText }}
                title="Narrower text"
                type="button"
              >
                <AlignRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleMarginChange(false)}
                disabled={marginWidth <= 50}
                className="p-1 rounded transition-opacity disabled:opacity-30 hover:opacity-70"
                style={{ color: theme.subText }}
                title="Wider text"
                type="button"
              >
                <AlignLeft className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Right: Mode + Auto-scroll + Options */}
          <div className="flex items-center gap-2">
            {/* Word count — desktop, scroll mode only (§3.3): authoring-time data;
                the folio supersedes it live. */}
            {viewMode !== 'book' && (
              <div
                className="hidden lg:flex items-center gap-1.5 text-xs px-2 py-1 rounded-md"
                style={{ color: theme.subText, background: theme.border }}
              >
                <FileText className="h-3 w-3" />
                <span title="Estimated preaching time">{wordCount}w · ~{preachTime}m</span>
              </div>
            )}

            {/* View mode pill (§3.1) — lives in the auto-scroll button's slot; book
                mode hides auto-scroll, so the bar's net growth stays ~zero. */}
            <div
              className="flex items-center rounded-lg p-0.5 gap-0.5"
              style={{ background: theme.border }}
            >
              {(['scroll', 'book'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => changeViewMode(mode)}
                  className="p-1.5 rounded-md transition-all duration-200"
                  style={{
                    background: viewMode === mode ? theme.controlsBg : 'transparent',
                    color: viewMode === mode ? theme.text : theme.subText,
                    boxShadow: viewMode === mode ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                  title={mode === 'scroll' ? 'Scroll mode' : 'Book mode (pages)'}
                  type="button"
                >
                  {mode === 'scroll'
                    ? <ScrollText className="h-4 w-4" />
                    : <BookOpen className="h-4 w-4" />}
                </button>
              ))}
            </div>

            {/* Auto-scroll toggle — scroll mode only */}
            {viewMode !== 'book' && (
            <button
              onClick={() => setAutoScroll(a => !a)}
              className="p-1.5 rounded-md transition-all"
              style={{
                color: autoScroll ? '#2874A6' : theme.subText,
                background: autoScroll ? 'rgba(40,116,166,0.12)' : 'transparent',
              }}
              title={autoScroll ? 'Stop auto-scroll' : 'Start auto-scroll (teleprompter)'}
              type="button"
            >
              {autoScroll ? <PauseCircle className="h-5 w-5" /> : <PlayCircle className="h-5 w-5" />}
            </button>
            )}

            {/* Options menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="flex items-center gap-1 px-2 py-1.5 rounded-md text-sm transition-all hover:opacity-80"
                style={{ color: theme.text, background: theme.border }}
                type="button"
              >
                <MoreVertical className="h-4 w-4" />
                <span className="hidden md:inline text-xs">Options</span>
              </button>

              <AnimatePresence>
                {showMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-1.5 w-48 rounded-xl shadow-xl z-50 overflow-hidden"
                    style={{ background: theme.infoBg, border: `1px solid ${theme.border}` }}
                  >
                    <div className="py-1">
                      {/* Columns (§3.2) — set-once lives in Options, deliberately NOT
                          a toolbar control that would pop in and out with the mode.
                          The ~24ch safety clamp in usePagedReader may still render 1
                          on narrow screens; the choice persists regardless. */}
                      {viewMode === 'book' && (
                        <div
                          className="flex items-center justify-between px-4 py-2.5 text-sm"
                          style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}
                        >
                          <span>Columns</span>
                          <div
                            className="flex items-center rounded-lg p-0.5 gap-0.5"
                            style={{ background: theme.border }}
                          >
                            {([1, 2] as const).map(n => (
                              <button
                                key={n}
                                onClick={() => changeBookColumns(n)}
                                className="px-2.5 py-1 rounded-md text-xs font-semibold transition-all duration-200"
                                style={{
                                  background: bookColumns === n ? theme.controlsBg : 'transparent',
                                  color: bookColumns === n ? theme.text : theme.subText,
                                  boxShadow: bookColumns === n ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                }}
                                title={n === 1 ? 'Single column' : 'Two-column spread'}
                                type="button"
                              >
                                {n}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <button
                        onClick={handleDownloadPDF}
                        disabled={exportLoading === 'pdf'}
                        className="flex items-center w-full px-4 py-2.5 text-sm transition-opacity hover:opacity-70"
                        style={{ color: theme.text }}
                        type="button"
                      >
                        {exportLoading === 'pdf'
                          ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating PDF...</>
                          : <><Download className="h-4 w-4 mr-2" />Download PDF</>}
                      </button>
                      <button
                        onClick={handleDownloadWord}
                        disabled={exportLoading === 'word'}
                        className="flex items-center w-full px-4 py-2.5 text-sm transition-opacity hover:opacity-70"
                        style={{ color: theme.text }}
                        type="button"
                      >
                        {exportLoading === 'word'
                          ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating Word...</>
                          : <><FileWord className="h-4 w-4 mr-2" />Download Word</>}
                      </button>
                      <button
                        onClick={handlePrint}
                        className="flex items-center w-full px-4 py-2.5 text-sm transition-opacity hover:opacity-70"
                        style={{ color: theme.text }}
                        type="button"
                      >
                        <Printer className="h-4 w-4 mr-2" />Print
                      </button>
                      {id !== 'preview' && !isPublic && (
                        <button
                          onClick={handleShare}
                          className="flex items-center w-full px-4 py-2.5 text-sm transition-opacity hover:opacity-70"
                          style={{ color: theme.text }}
                          type="button"
                        >
                          <LinkIcon className="h-4 w-4 mr-2" />
                          {copying ? 'Link Copied!' : 'Copy Share Link'}
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* ── Info Bar ─────────────────────────────────────────────────── */}
      <div
        ref={infoBarRef}
        className="fixed left-0 right-0 z-40 transition-all duration-300"
        onPointerDownCapture={viewMode === 'book' ? () => setChromeIdleTick(t => t + 1) : undefined}
        style={{
          // Both modes keep the info bar pinned (title, timer, progress) and slide
          // it to the top when the controls row hides. In book mode the stage sits
          // exactly below it (topOffset = infoBarHeight), so hidden controls leave
          // the info bar flush above the page with no dead strip.
          top: controlsHidden ? 0 : controlsHeight,
          background: theme.infoBg,
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h1
              className="font-bold line-clamp-1 leading-tight"
              style={{
                fontSize: isMobile ? '1.1rem' : '1.35rem',
                color: theme.text,
                fontFamily: fontFamily === 'serif' ? 'Georgia, "Times New Roman", serif' : 'inherit',
              }}
            >
              {title}
            </h1>
            {(scripture || date) && (
              <div className="flex items-center gap-3 mt-0.5">
                {scripture && (
                  <span className="text-sm font-medium" style={{ color: theme.subText }}>{scripture}</span>
                )}
                {date && (
                  <span className="text-xs" style={{ color: theme.subText }}>
                    {parseISO(date).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <Timer />
            {(viewMode === 'book' || sectionCount > 0) && (
              <div className="hidden md:flex items-center gap-2">
                <div className="relative w-9 h-9">
                  <svg className="transform -rotate-90 w-9 h-9">
                    <circle cx="18" cy="18" r="14" stroke={theme.border} strokeWidth="2" fill="none" />
                    <circle
                      cx="18" cy="18" r="14"
                      stroke="#2874A6" strokeWidth="2" fill="none"
                      strokeDasharray={`${2 * Math.PI * 14}`}
                      strokeDashoffset={2 * Math.PI * 14 * (1 - progress / 100)}
                      className="transition-all duration-150"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] font-semibold tabular-nums" style={{ color: theme.subText }}>
                      {Math.round(progress)}%
                    </span>
                  </div>
                </div>
                <div className="text-xs leading-tight" style={{ color: theme.subText }}>
                  {viewMode === 'book' ? (
                    <>
                      <div>Page {bookPage + 1}</div>
                      <div>of {bookPageCount}</div>
                    </>
                  ) : (
                    <>
                      <div>§{currentSectionIndex + 1}</div>
                      <div>of {sectionCount}</div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Scroll progress bar — cut in book mode (§4.3): with the folio plus the
            ring, a third indicator is clutter. */}
        {viewMode !== 'book' && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: theme.border }}>
            <div
              className="h-full transition-all duration-150 ease-out"
              style={{ width: `${scrollProgress}%`, background: '#2874A6' }}
            />
          </div>
        )}

        {shareError && (
          <div className="mx-4 mb-2 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {shareError}
          </div>
        )}
      </div>

      {/* ── Sermon Content ────────────────────────────────────────────── */}
      {viewMode === 'book' ? (
        <BookReader
          // Remount per sermon: a same-route id change (browser back/forward between
          // two preach URLs) swaps `blocks` under a mounted reader, which would keep
          // stale geometry — a fresh mount re-measures and starts at page 1.
          key={id ?? 'preview'}
          blocks={processedContent}
          styles={styles}
          readingMode={readingMode}
          columns={bookColumns}
          fontSize={fontSize}
          fontFamily={fontFamily}
          topOffset={infoBarHeight}
          {...contentLayout(marginWidth, isMobile)}
          theme={theme}
          startedAt={preachStartedAt}
          onToggleChrome={toggleChrome}
          onPageState={handleBookPageState}
          onUserNavigate={wakeLock.retry}
        />
      ) : (
      <div
        ref={contentContainerRef}
        className="mx-auto transition-all duration-300 ease-in-out"
        style={{
          paddingTop: '2.5rem',
          paddingBottom: isMobile ? '6rem' : '80px',
          paddingLeft: isMobile ? '0.875rem' : '1.25rem',
          paddingRight: isMobile ? '0.875rem' : '1.25rem',
          width: isMobile ? '92%' : '70%',
          maxWidth: isMobile ? 'none' : '1200px',
        }}
      >
        <div
          className="prose prose-lg max-w-none"
          style={{
            '--base-font-size': `${fontSize}%`,
            fontSize: 'var(--base-font-size)',
            fontFamily: fontFamily === 'serif'
              ? 'Georgia, "Times New Roman", serif'
              : 'inherit',
          } as React.CSSProperties}
        >
          <SermonBlocks
            blocks={processedContent}
            variant="card"
            styles={styles}
            readingMode={readingMode}
          />
        </div>
      </div>
      )}

      {/* ── Auto-scroll speed control (floating) ─────────────────────── */}
      <AnimatePresence>
        {autoScroll && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 rounded-full shadow-xl"
            style={{
              background: theme.controlsBg,
              border: `1px solid ${theme.border}`,
              backdropFilter: 'blur(12px)',
            }}
          >
            <button
              onClick={() => setScrollSpeed(s => Math.max(1, s - 1))}
              className="p-1 rounded-full transition-opacity hover:opacity-70"
              style={{ color: theme.subText }}
              type="button"
            >
              <MinusCircle className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-1.5">
              <PlayCircle className="h-4 w-4" style={{ color: '#2874A6' }} />
              <span className="text-xs font-semibold tabular-nums" style={{ color: theme.text }}>
                Speed {scrollSpeed}
              </span>
            </div>
            <button
              onClick={() => setScrollSpeed(s => Math.min(10, s + 1))}
              className="p-1 rounded-full transition-opacity hover:opacity-70"
              style={{ color: theme.subText }}
              type="button"
            >
              <PlusCircle className="h-4 w-4" />
            </button>
            <div className="w-px h-4 mx-1" style={{ background: theme.border }} />
            <button
              onClick={() => setAutoScroll(false)}
              className="text-xs font-medium transition-opacity hover:opacity-70"
              style={{ color: theme.subText }}
              type="button"
            >
              Stop
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {showShareModal && !isPublic && (
        <SermonShareModal
          sermonId={id}
          sermonTitle={title}
          sermonScripture={scripture}
          sermonContent={content}
          youtubeUrl={youtubeUrl || ''}
          sermonDate={date}
          onClose={() => setShowShareModal(false)}
        />
      )}

      <style>{`
        @media print {
          .prose { font-size: 12pt !important; }
          h1 { font-size: 24pt !important; }
          h2 { font-size: 18pt !important; }
          h3 { font-size: 14pt !important; }
          .sermon-section { break-inside: avoid; }
          button { display: none !important; }
        }

        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
        }

        /* Book mode measures geometry that this transition would animate — a naive
           measure reads a layout the browser is only passing through (§2.3). */
        .prose { transition: ${viewMode === 'book' ? 'none' : 'font-size 0.2s ease-in-out'}; }

        .prose :where(h1, h2, h3) {
          font-family: ${fontFamily === 'serif'
            ? '"DM Serif Display", Georgia, "Times New Roman", serif'
            : '"DM Serif Display", serif'} !important;
        }

        /* Book mode scales headings with the font setting (§5.2): a hard-coded
           2.4rem h1 in a half-width column at large font overflows and clips.
           Scroll mode keeps the fixed sizes byte-for-byte. */
        .prose :where(h1):not(:where([class~=not-prose] *)) {
          font-size: ${viewMode === 'book' ? 'calc(2.4rem * var(--base-font-size) / 100)' : '2.4rem'} !important;
          line-height: 1.15 !important;
          margin-bottom: 1.5rem !important;
          font-weight: 800 !important;
          color: ${theme.text} !important;
        }
        .prose :where(h2):not(:where([class~=not-prose] *)) {
          font-size: ${viewMode === 'book' ? 'calc(1.85rem * var(--base-font-size) / 100)' : '1.85rem'} !important;
          line-height: 1.25 !important;
          margin-bottom: 1.25rem !important;
          font-weight: 700 !important;
          color: ${theme.text} !important;
        }
        .prose :where(h3):not(:where([class~=not-prose] *)) {
          font-size: ${viewMode === 'book' ? 'calc(1.45rem * var(--base-font-size) / 100)' : '1.45rem'} !important;
          line-height: 1.35 !important;
          margin-bottom: 1rem !important;
          font-weight: 600 !important;
          color: ${theme.text} !important;
        }
        .prose :where(p):not(:where([class~=not-prose] *)) {
          font-size: calc(1rem * var(--base-font-size) / 100) !important;
          line-height: 1.9 !important;
          margin-bottom: 1.15rem;
          color: ${theme.text};
          letter-spacing: ${fontSize > 140 ? '0.01em' : 'normal'};
        }
        .prose :where(li):not(:where([class~=not-prose] *)) {
          color: ${theme.text} !important;
          line-height: 1.85 !important;
        }
        .prose :where(ul li):not(:where([class~=not-prose] *))::marker,
        .prose :where(ol li):not(:where([class~=not-prose] *))::marker {
          color: ${theme.subText} !important;
        }
        .prose :where(a):not(:where([class~=not-prose] *)) {
          color: #2874A6 !important;
          text-decoration: underline;
          cursor: pointer;
        }
        .prose ul, .prose ol {
          font-size: calc(1rem * var(--base-font-size) / 100);
          line-height: 1.85;
        }
        .prose blockquote {
          font-size: calc(1.15rem * var(--base-font-size) / 100);
          border-left-color: ${theme.border};
          color: ${theme.subText} !important;
          font-style: italic;
        }
        .prose strong[style*="color"], .prose b[style*="color"],
        .prose em[style*="color"], .prose span[style*="color"] strong {
          color: inherit !important;
        }
        ${readingMode === 'warm' ? `
          .prose mark, .prose span[style*="background-color"] {
            filter: sepia(0.3) saturate(0.9);
          }
        ` : ''}
        ${readingMode === 'dark' ? `
          .prose mark, .prose span[style*="background-color"] {
            filter: saturate(2.5) brightness(0.7);
            color: #eee !important;
          }
        ` : ''}
      `}</style>
    </div>
  );
}
